import { ChipProps } from '@mui/material/Chip';

/**
 * Formats a date string to a more readable format
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-PY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Formats a date string without timezone conversion (for invoice dates)
 * @param dateString - ISO date string (YYYY-MM-DD format)
 * @returns Formatted date string
 */
export const formatDateOnly = (dateString: string): string => {
  if (!dateString) return 'N/A';
  
  try {
    // Parse date as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return new Intl.DateTimeFormat('es-PY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date only:', error);
    return dateString;
  }
};

/**
 * Formats a number as currency
 * @param amount - The amount to format
 * @param currency - The currency code (default: PYG)
 * @returns Formatted currency string
 */
const CURRENCY_ALIASES: Record<string, string> = {
  'Gs.': 'PYG',
  'Gs': 'PYG',
  'gs.': 'PYG',
  'gs': 'PYG',
  'USD$': 'USD',
};

export const formatCurrency = (amount: number | string, currency: string = 'PYG'): string => {
  if (amount === null || amount === undefined) return 'N/A';
  
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) return 'N/A';

  // Normalize common non-ISO currency aliases
  const isoCurrency = CURRENCY_ALIASES[currency] || currency;
  
  try {
    // Special formatting for Paraguayan Guaraní (no decimals)
    if (isoCurrency === 'PYG') {
      return new Intl.NumberFormat('es-PY', {
        style: 'currency',
        currency: 'PYG',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericAmount);
    }
    
    // Default formatting for other currencies
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: isoCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${numericAmount} ${currency}`;
  }
};

/**
 * Calculates the days remaining until a due date
 * @param dueDate - ISO date string of the due date
 * @returns Number of days remaining (negative if past due)
 */
export const getDaysRemaining = (dueDate: string): number => {
  if (!dueDate) return 0;
  
  try {
    const due = new Date(dueDate);
    const today = new Date();
    
    // Reset time portion for accurate day calculation
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating days remaining:', error);
    return 0;
  }
};

/**
 * Returns a descriptive object for days remaining with text and color
 * @param daysRemaining - Number of days remaining
 * @returns Object with text and Chip color
 */
export const getDueStatusText = (daysRemaining: number): { text: string; color: ChipProps['color'] } => {
  if (daysRemaining < 0) {
    return { text: `Overdue by ${Math.abs(daysRemaining)} days`, color: 'error' };
  } else if (daysRemaining === 0) {
    return { text: 'Due today', color: 'warning' };
  } else if (daysRemaining <= 7) {
    return { text: `Due in ${daysRemaining} days`, color: 'primary' };
  } else {
    return { text: `Due in ${daysRemaining} days`, color: 'default' };
  }
};
